import React, { useEffect, useRef, useState } from 'react';
import { Dialog, Box, Typography, Button } from '@mui/material';
import { Check, Copy, ShieldCheck } from 'lucide-react';
import { TwoFactorChallenge } from 'types/auth';
import styles from './TwoFactorModal.module.scss';

interface TwoFactorModalProps {
  open: boolean;
  challenge: TwoFactorChallenge;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (code: string) => void;
}

const CODE_LENGTH = 6;

/**
 * Second login step. Two modes off the same challenge:
 *
 *  - `setup`  the admin has never enrolled — shows the QR to scan (plus the
 *             secret to type in by hand if the camera won't cooperate) and
 *             takes the first code, which both enrolls them and logs them in.
 *  - `verify` already enrolled — just the code.
 *
 * In setup mode the dialog ignores backdrop clicks and ESC: closing it
 * mid-enrollment by accident is the one mistake worth designing out. Cancel
 * is still there, and it costs nothing — logging in again returns the same
 * QR.
 */
const TwoFactorModal: React.FC<TwoFactorModalProps> = ({
  open,
  challenge,
  loading,
  error,
  onClose,
  onSubmit,
}) => {
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isSetup = challenge.mode === 'setup';
  const isValid = code.length === CODE_LENGTH;

  useEffect(() => {
    if (open) {
      setCode('');
      setCopied(false);
      // Defer to the dialog's own mount transition, otherwise the focus is
      // taken back by the paper element.
      const id = window.setTimeout(() => inputRef.current?.focus(), 120);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || loading) return;
    onSubmit(code);
  };

  const handleCopySecret = async () => {
    if (!challenge.secret) return;
    try {
      await navigator.clipboard.writeText(challenge.secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard permission denied — the secret is on screen to read anyway.
    }
  };

  return (
    <Dialog
      open={open}
      // Setup mode: no backdrop / ESC dismissal, so a stray click can't
      // interrupt enrollment. Verify mode keeps the usual behaviour.
      onClose={loading || isSetup ? undefined : onClose}
      classes={{ paper: styles.dialogPaper }}
      disableRestoreFocus
    >
      <Box className={styles.header}>
        <ShieldCheck size={20} className={styles.headerIcon} />
        <Typography variant="h6" className={styles.title}>
          {isSetup ? 'Set up two-factor authentication' : 'Two-factor authentication'}
        </Typography>
      </Box>

      <Typography variant="body2" className={styles.description}>
        {isSetup
          ? 'Scan this QR code with Google Authenticator, Authy or any TOTP app, then enter the 6-digit code it shows.'
          : 'Enter the 6-digit code from your authenticator app to finish signing in.'}
      </Typography>

      <form onSubmit={handleSubmit}>
        {isSetup && challenge.qrDataUrl && (
          <Box className={styles.qrBlock}>
            <img src={challenge.qrDataUrl} alt="Authenticator QR code" className={styles.qr} />

            {challenge.secret && (
              <Box className={styles.secretBlock}>
                <span className={styles.secretLabel}>Can&apos;t scan? Enter this key:</span>
                <Box className={styles.secretRow}>
                  <code className={styles.secret}>{challenge.secret}</code>
                  <button
                    type="button"
                    className={styles.copyBtn}
                    onClick={handleCopySecret}
                    aria-label="Copy setup key"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Box className={styles.codeBlock}>
          <label htmlFor="twoFactorCode" className={styles.codeLabel}>
            Authentication code
          </label>
          <input
            id="twoFactorCode"
            ref={inputRef}
            className={`${styles.codeInput} ${error ? styles.codeInputError : ''}`}
            value={code}
            onChange={handleChange}
            placeholder="------"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={CODE_LENGTH}
            disabled={loading}
          />
          {error && <span className={styles.errorMsg}>{error}</span>}
        </Box>

        <Box className={styles.actions}>
          <Button onClick={onClose} className={styles.cancelBtn} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            className={styles.confirmBtn}
            disabled={loading || !isValid}
          >
            {loading ? 'Verifying...' : isSetup ? 'Activate & sign in' : 'Verify'}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default TwoFactorModal;
